import React from 'react';

import { Platform, StyleSheet } from 'react-native';
import type { ViewStyle } from 'react-native';

import Picture from 'components/picture';
import { Box, Typography, withTypography } from 'components/ui';
import type { BoxProps, TypographyProps } from 'components/ui';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { getDesignToken, primitive, spacing } from 'styles/constants';

const MENU_TILE_GRID_GAP = 24;
export const MENU_TILE_GRID_HALF_GAP = MENU_TILE_GRID_GAP / 2;

const MENU_IMAGE_MOBILE_WIDTH = 104;
const MENU_IMAGE_DESKTOP_WIDTH = 248;
const isWeb = Platform.OS === 'web';

type TileFooterProps = BoxProps & { isMenuItem: boolean };
type TileImageProps = React.ComponentProps<typeof Picture>;

const webPointerStyle = { cursor: 'pointer' } as ViewStyle;
const tileCardWebStyle = ({
  maxWidth: `calc(33% - ${MENU_TILE_GRID_GAP}px)`,
} as unknown) as ViewStyle;

export const PriceAndCaloriesListWrapper = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.priceAndCaloriesListWrapper, style],
  });

export const Tile = ({ style, ...props }: BoxProps) => {
  const isDesktop = useIsDesktopBreakpoint();

  return React.createElement(Box, {
    ...props,
    style: [styles.tile, isDesktop && styles.tileDesktop, isWeb && webPointerStyle, style],
  });
};

export const TileCard = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.tileCard, isWeb && tileCardWebStyle, style],
  });

export const TileFooter = ({ isMenuItem, style, ...props }: TileFooterProps) =>
  React.createElement(Box, {
    ...props,
    style: [
      styles.tileFooter,
      isMenuItem ? styles.tileFooterMenuItem : styles.tileFooterSection,
      style,
    ],
  });

export const TileHeader = ({ style, ...props }: TypographyProps) => {
  const isDesktop = useIsDesktopBreakpoint();

  return (
    <Typography
      variant="headerTwo"
      fontSize={isDesktop ? '2xl' : 'lg'}
      m="$0"
      numberOfLines={2} // causes ellipsis
      style={[
        { textTransform: 'none' },
        Platform.OS === 'web' && { lineHeight: ('normal' as unknown) as number },
        style,
      ]}
      {...props}
    />
  );
};

export const TileDescription = ({ style, ...props }: BoxProps) => {
  const isDesktop = useIsDesktopBreakpoint();

  return React.createElement(Box, {
    ...props,
    style: [styles.tileDescription, isDesktop && styles.tileDescriptionDesktop, style],
  });
};

export const TileImageWrapper = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.tileImageWrapper, style],
  });

export const TileImage = (props: TileImageProps) => {
  const isDesktop = useIsDesktopBreakpoint();

  return React.createElement(Picture, {
    ...props,
    width: isDesktop ? MENU_IMAGE_DESKTOP_WIDTH : MENU_IMAGE_MOBILE_WIDTH,
    height: isDesktop ? MENU_IMAGE_DESKTOP_WIDTH : MENU_IMAGE_MOBILE_WIDTH,
    borderTopRadius: primitive.$borderRadius,
    borderBottomRadius: isDesktop ? 0 : primitive.$borderRadius,
  });
};

export const ItemPrice = withTypography({
  bold: true,
  color: getDesignToken('color-pricing'),
});

export const StyledItemPrice = withTypography({
  color: primitive.$bbqBrown,
  numberOfLines: 1,
  bold: true,
});

export const StyledPriceAndCaloriesListWrapper = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.styledPriceAndCaloriesListWrapper, style],
  });

export const StyledListItemCalories = withTypography({
  mb: '$0',
  mt: '$0',
  color: primitive.$bbqBrown,
  numberOfLines: 1,
  style: {
    textTransform: 'capitalize',
  },
});

export const StyledMenuSectionTitle = withTypography({
  variant: 'headerThree',
  fontSize: 'lg',
  lineHeight: 20,
  color: getDesignToken('text-default'),
  textAlign: 'center',
  m: '$0',
});

const styles = StyleSheet.create({
  priceAndCaloriesListWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.$2,
    justifyContent: 'flex-start',
  },
  styledPriceAndCaloriesListWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.$2,
    justifyContent: 'center',
  },
  tile: {
    alignItems: 'center',
    flexDirection: 'row',
    flexGrow: 1,
    flexShrink: 1,
    height: 112,
    paddingHorizontal: spacing.$4,
    paddingVertical: spacing.$2,
  },
  tileDesktop: {
    minHeight: spacing.$80,
  },
  tileCard: {
    alignItems: 'center',
    borderRadius: 12,
    flex: 1,
    justifyContent: 'center',
    margin: MENU_TILE_GRID_HALF_GAP,
    minHeight: 220,
  },
  tileDescription: {
    flexShrink: 1,
    paddingHorizontal: spacing.$3,
    paddingVertical: spacing.$0,
  },
  tileDescriptionDesktop: {
    paddingHorizontal: spacing.$8,
    paddingVertical: spacing.$5,
  },
  tileFooter: {
    alignItems: 'center',
    borderBottomLeftRadius: primitive.$borderRadius,
    borderBottomRightRadius: primitive.$borderRadius,
    justifyContent: 'center',
  },
  tileFooterMenuItem: {
    height: 92,
  },
  tileFooterSection: {
    height: 54,
  },
  tileHeader: {
    margin: spacing.$0,
    textTransform: 'none',
  },
  tileImageWrapper: {
    minWidth: MENU_IMAGE_MOBILE_WIDTH,
  },
});
